import { goldenHourStart } from '../lib/solar.ts';
import { localDate, localHour } from '../lib/time.ts';
import type { GeoPoint, IsoDateTime } from './common.ts';
import type { SourceRef } from './source.ts';

/**
 * « Le ciel d'Ermont » : potentiel du coucher de soleil et indices du quotidien (linge, parapluie,
 * veste, vélo).
 *
 * Ce sont des ESTIMATIONS d'Ermont Live, calculées à partir d'une prévision météo : jamais une
 * prévision officielle. La méthode est décrite dans docs/09-ciel-et-indices.md et chaque résultat
 * expose ses facteurs pour que l'utilisateur voie pourquoi.
 */

/**
 * Une heure de prévision. Nuages, températures, humidité, vent : valeur instantanée ;
 * pluie et soleil : cumul de l'heure PRÉCÉDENTE ; rafales : maximum de l'heure précédente.
 */
export interface HourlyForecast {
  readonly time: IsoDateTime;
  readonly cloudLowPct: number;
  readonly cloudMidPct: number;
  readonly cloudHighPct: number;
  readonly precipitationMm: number;
  readonly humidityPct: number;
  readonly windKmh: number;
  readonly sunshineS: number;
  readonly temperatureC: number;
  readonly feelsLikeC: number;
  readonly gustKmh: number;
}

export interface CloudLayers {
  readonly lowPct: number;
  readonly midPct: number;
  readonly highPct: number;
}

export interface SkyForecast {
  /** Point de la prévision locale (sert au calcul de l'heure dorée). */
  readonly location: GeoPoint;
  readonly hours: readonly HourlyForecast[];
  /** Nuages à l'horizon, vers le soleil couchant : c'est là que passe la lumière du coucher. */
  readonly horizon: {
    readonly point: GeoPoint;
    readonly bearingDeg: number;
    readonly distanceKm: number;
    readonly hours: readonly (CloudLayers & { readonly time: IsoDateTime })[];
  };
  readonly days: readonly { readonly sunrise: IsoDateTime; readonly sunset: IsoDateTime }[];
  readonly source: SourceRef;
}

// ── Coucher de soleil ─────────────────────────────────────────────────────────

export type SunsetLevel = 'dull' | 'modest' | 'good' | 'great';

export type SunsetFactor =
  | 'clear_sky'
  | 'high_clouds'
  | 'mid_clouds'
  | 'thick_clouds'
  | 'horizon_clear'
  | 'horizon_blocked'
  | 'low_clouds'
  | 'rain';

export interface SunsetOutlook {
  readonly sunsetAt: IsoDateTime;
  /** Début de l'heure dorée : soleil à 6° au-dessus de l'horizon (calcul astronomique). */
  readonly goldenHourAt: IsoDateTime;
  readonly day: 'today' | 'tomorrow';
  /** 0–100 : sert au classement, jamais affiché comme une mesure. */
  readonly score: number;
  readonly level: SunsetLevel;
  readonly factors: readonly SunsetFactor[];
  readonly overhead: CloudLayers;
  readonly horizon: CloudLayers & { readonly bearingDeg: number; readonly distanceKm: number };
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/** Interpolation linéaire par morceaux sur des points (x croissants). */
function piecewise(x: number, points: readonly (readonly [number, number])[]): number {
  const first = points[0];
  const last = points.at(-1);
  if (first === undefined || last === undefined) return 0;
  if (x <= first[0]) return first[1];
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i] as readonly [number, number];
    const [x0, y0] = points[i - 1] as readonly [number, number];
    if (x <= x1) return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
  }
  return last[1];
}

const HOUR_MS = 3_600_000;

function nearest<T extends { readonly time: IsoDateTime }>(items: readonly T[], instant: number): T | undefined {
  let best: T | undefined;
  for (const item of items) {
    const gap = Math.abs(Date.parse(item.time) - instant);
    if (gap <= HOUR_MS / 2 && (best === undefined || gap < Math.abs(Date.parse(best.time) - instant))) best = item;
  }
  return best;
}

/** Le coucher affiché bascule sur le lendemain une fois celui du jour passé depuis 20 min. */
const SUNSET_GRACE_MS = 20 * 60_000;

export function sunsetOutlook(forecast: SkyForecast, now: Date, timeZone: string): SunsetOutlook | null {
  const today = localDate(now, timeZone);
  const next = forecast.days
    .map((d) => d.sunset)
    .toSorted()
    .find((s) => Date.parse(s) + SUNSET_GRACE_MS > now.getTime());
  if (next === undefined) return null;
  const t = Date.parse(next);

  const local = nearest(forecast.hours, t);
  const far = nearest(forecast.horizon.hours, t);
  if (local === undefined || far === undefined) return null;
  // Pluie pendant l'heure qui contient le coucher : valeur horodatée à la fin de cette heure.
  const rainHour = forecast.hours.find((h) => {
    const end = Date.parse(h.time);
    return end > t && end - HOUR_MS <= t;
  });

  // « Toile » : les nuages moyens et hauts au-dessus de nous, que la lumière rasante colore.
  const canvas = Math.min(100, local.cloudHighPct + 0.7 * local.cloudMidPct);
  const canvasFactor = piecewise(canvas, [
    [0, 0.3],
    [30, 0.9],
    [50, 1],
    [80, 1],
    [100, 0.8],
  ]);
  // Chemin de la lumière : des nuages bas ou moyens à l'horizon l'interceptent.
  const blocking = Math.max(far.lowPct, 0.8 * far.midPct) / 100;
  const lightFactor = 1 - 0.9 * blocking;
  // Nuages bas sur Ermont : ils cachent le ciel coloré.
  const lowFactor = 1 - 0.8 * (local.cloudLowPct / 100);
  const thick = local.cloudMidPct > 80;
  const raining = (rainHour?.precipitationMm ?? 0) >= 0.2;
  const score = Math.round(100 * canvasFactor * lightFactor * lowFactor * (thick ? 0.7 : 1) * (raining ? 0.4 : 1));

  const factors: SunsetFactor[] = [];
  if (canvas < 10) factors.push('clear_sky');
  else if (canvas >= 30) factors.push(local.cloudHighPct >= local.cloudMidPct ? 'high_clouds' : 'mid_clouds');
  if (thick) factors.push('thick_clouds');
  if (blocking >= 0.5) factors.push('horizon_blocked');
  else if (blocking < 0.3 && canvas >= 30) factors.push('horizon_clear');
  if (local.cloudLowPct >= 50) factors.push('low_clouds');
  if (raining) factors.push('rain');

  return {
    sunsetAt: new Date(t).toISOString(),
    goldenHourAt: goldenHourStart(new Date(t), forecast.location.lat).toISOString(),
    day: localDate(new Date(t), timeZone) === today ? 'today' : 'tomorrow',
    score,
    level: score >= 75 ? 'great' : score >= 50 ? 'good' : score >= 25 ? 'modest' : 'dull',
    factors,
    overhead: { lowPct: local.cloudLowPct, midPct: local.cloudMidPct, highPct: local.cloudHighPct },
    horizon: {
      lowPct: far.lowPct,
      midPct: far.midPct,
      highPct: far.highPct,
      bearingDeg: Math.round(forecast.horizon.bearingDeg),
      distanceKm: forecast.horizon.distanceKm,
    },
  };
}

// ── Indices du quotidien ──────────────────────────────────────────────────────

export type LaundryLevel = 'excellent' | 'good' | 'fair' | 'slow' | 'risky' | 'avoid';
export type UmbrellaLevel = 'useless' | 'handy' | 'needed';
export type JacketLevel = 'none' | 'light' | 'jacket' | 'coat' | 'warm_coat';
export type BikeLevel = 'ideal' | 'good' | 'fair' | 'avoid';

export type DailyIndex =
  | {
      readonly kind: 'laundry';
      readonly day: 'today' | 'tomorrow';
      readonly level: LaundryLevel;
      /** Début de la première heure pluvieuse de la plage 10h–17h, s'il y en a une. */
      readonly rainFrom?: IsoDateTime;
    }
  | {
      readonly kind: 'umbrella';
      readonly day: 'today' | 'tomorrow';
      readonly level: UmbrellaLevel;
      readonly rainFrom?: IsoDateTime;
      readonly rainUntil?: IsoDateTime;
      readonly totalMm: number;
    }
  | {
      readonly kind: 'jacket';
      readonly day: 'today' | 'tomorrow';
      readonly level: JacketLevel;
      readonly feelsLikeMinC: number;
      readonly feelsLikeMaxC: number;
    }
  | {
      readonly kind: 'bike';
      readonly day: 'today' | 'tomorrow';
      readonly level: BikeLevel;
      readonly maxGustKmh: number;
      readonly rainFrom?: IsoDateTime;
    };

/** Seuil de pluie significative sur une heure (en dessous : quelques gouttes au plus). */
const WET_MM = 0.1;

/** Heures dont la période (heure précédant l'horodatage) tombe dans [from, to[ heures locales du jour `date`. */
function hoursOfDay(
  hours: readonly HourlyForecast[],
  date: string,
  from: number,
  to: number,
  timeZone: string,
): HourlyForecast[] {
  return hours.filter((h) => {
    const start = new Date(Date.parse(h.time) - HOUR_MS);
    if (localDate(start, timeZone) !== date) return false;
    const hour = localHour(start, timeZone);
    return hour >= from && hour < to;
  });
}

const periodStart = (h: HourlyForecast) => new Date(Date.parse(h.time) - HOUR_MS).toISOString();

function nextDate(now: Date, timeZone: string): string {
  return localDate(new Date(now.getTime() + 24 * HOUR_MS), timeZone);
}

/** Linge dehors : plage 10h–17h ; à partir de 14h, on parle de demain. */
function laundryIndex(forecast: SkyForecast, now: Date, timeZone: string): DailyIndex | null {
  const day = localHour(now, timeZone) >= 14 ? 'tomorrow' : 'today';
  const date = day === 'today' ? localDate(now, timeZone) : nextDate(now, timeZone);
  const hours = hoursOfDay(forecast.hours, date, 10, 17, timeZone);
  if (hours.length < 5) return null;

  const wet = hours.filter((h) => h.precipitationMm >= WET_MM);
  const rain = hours.reduce((sum, h) => sum + h.precipitationMm, 0);
  const firstWet = wet[0];
  if (firstWet !== undefined) {
    const level = rain >= 0.5 || wet.length >= 2 ? 'avoid' : 'risky';
    return { kind: 'laundry', day, level, rainFrom: periodStart(firstWet) };
  }

  const mean = (pick: (h: HourlyForecast) => number) => hours.reduce((s, h) => s + pick(h), 0) / hours.length;
  const sun = clamp01(hours.reduce((s, h) => s + h.sunshineS, 0) / (hours.length * 3600));
  const dry = clamp01((90 - mean((h) => h.humidityPct)) / 60);
  const wind = clamp01(mean((h) => h.windKmh) / 20);
  const warm = clamp01((mean((h) => h.temperatureC) - 5) / 20);
  const score = 0.4 * sun + 0.3 * dry + 0.15 * wind + 0.15 * warm;
  const level = score >= 0.7 ? 'excellent' : score >= 0.5 ? 'good' : score >= 0.3 ? 'fair' : 'slow';
  return { kind: 'laundry', day, level };
}

/**
 * Plage de sortie commune au parapluie, à la veste et au vélo : de maintenant à 22h ;
 * après 20h, la journée de demain (7h–22h).
 */
function outingWindow(
  forecast: SkyForecast,
  now: Date,
  timeZone: string,
): { day: 'today' | 'tomorrow'; hours: HourlyForecast[] } {
  const day = localHour(now, timeZone) >= 20 ? 'tomorrow' : 'today';
  const hours =
    day === 'today'
      ? hoursOfDay(forecast.hours, localDate(now, timeZone), 0, 22, timeZone).filter(
          (h) => Date.parse(h.time) > now.getTime(),
        )
      : hoursOfDay(forecast.hours, nextDate(now, timeZone), 7, 22, timeZone);
  return { day, hours };
}

/** Parapluie : pluie prévue sur la plage de sortie. */
function umbrellaIndex(forecast: SkyForecast, now: Date, timeZone: string): DailyIndex | null {
  const { day, hours } = outingWindow(forecast, now, timeZone);
  if (hours.length === 0) return null;

  const wet = hours.filter((h) => h.precipitationMm >= WET_MM);
  const totalMm = Math.round(hours.reduce((sum, h) => sum + h.precipitationMm, 0) * 10) / 10;
  const firstWet = wet[0];
  const lastWet = wet.at(-1);
  if (firstWet === undefined || lastWet === undefined) return { kind: 'umbrella', day, level: 'useless', totalMm };
  const max = Math.max(...wet.map((h) => h.precipitationMm));
  return {
    kind: 'umbrella',
    day,
    level: max < 0.5 && totalMm < 1 ? 'handy' : 'needed',
    rainFrom: periodStart(firstWet),
    rainUntil: new Date(Date.parse(lastWet.time)).toISOString(),
    totalMm,
  };
}

/** Veste : d'après la température ressentie la plus basse de la plage de sortie. */
function jacketIndex(forecast: SkyForecast, now: Date, timeZone: string): DailyIndex | null {
  const { day, hours } = outingWindow(forecast, now, timeZone);
  if (hours.length === 0) return null;
  const felt = hours.map((h) => h.feelsLikeC);
  const minC = Math.round(Math.min(...felt));
  const maxC = Math.round(Math.max(...felt));
  const level: JacketLevel =
    minC >= 22 ? 'none' : minC >= 16 ? 'light' : minC >= 10 ? 'jacket' : minC >= 3 ? 'coat' : 'warm_coat';
  return { kind: 'jacket', day, level, feelsLikeMinC: minC, feelsLikeMaxC: maxC };
}

/** Vélo : pluie, rafales et température ressentie sur la plage de sortie. */
function bikeIndex(forecast: SkyForecast, now: Date, timeZone: string): DailyIndex | null {
  const { day, hours } = outingWindow(forecast, now, timeZone);
  if (hours.length === 0) return null;
  const wet = hours.filter((h) => h.precipitationMm >= WET_MM);
  const rain = hours.reduce((sum, h) => sum + h.precipitationMm, 0);
  const maxGustKmh = Math.round(Math.max(...hours.map((h) => h.gustKmh)));
  const minFelt = Math.min(...hours.map((h) => h.feelsLikeC));
  const maxFelt = Math.max(...hours.map((h) => h.feelsLikeC));
  const firstWet = wet[0];

  let level: BikeLevel;
  if (rain >= 1 || maxGustKmh >= 60) level = 'avoid';
  else if (firstWet !== undefined || maxGustKmh >= 45 || minFelt < 0) level = 'fair';
  else if (maxGustKmh < 35 && minFelt >= 10 && maxFelt <= 26) level = 'ideal';
  else level = 'good';

  return {
    kind: 'bike',
    day,
    level,
    maxGustKmh,
    ...(firstWet === undefined ? {} : { rainFrom: periodStart(firstWet) }),
  };
}

/** Toujours dans le même ordre, pour une grille stable : linge, parapluie, veste, vélo. */
export function dailyIndices(forecast: SkyForecast, now: Date, timeZone: string): DailyIndex[] {
  return [
    laundryIndex(forecast, now, timeZone),
    umbrellaIndex(forecast, now, timeZone),
    jacketIndex(forecast, now, timeZone),
    bikeIndex(forecast, now, timeZone),
  ].filter((index): index is DailyIndex => index !== null);
}

export interface SkyOutlook {
  readonly sunset: SunsetOutlook | null;
  readonly indices: readonly DailyIndex[];
  readonly source: SourceRef;
}

export function skyOutlook(forecast: SkyForecast, now: Date, timeZone: string): SkyOutlook {
  return {
    sunset: sunsetOutlook(forecast, now, timeZone),
    indices: dailyIndices(forecast, now, timeZone),
    source: forecast.source,
  };
}

// ── Webcam de ciel ────────────────────────────────────────────────────────────

/**
 * Webcam tierce montrant le ciel près de la ville. Jamais présentée comme « le ciel d'Ermont » : son lieu, sa
 * distance et son orientation sont toujours affichés.
 */
export interface SkyWebcam {
  readonly name: string;
  /** Commune où se trouve la caméra. */
  readonly place: string;
  readonly distanceKm: number;
  /** Direction de la caméra depuis la ville (« au sud-ouest »). */
  readonly fromCity: string;
  /** Ce que la caméra regarde (« vers La Défense, à l'est-sud-est »). */
  readonly facing: string;
  /** Lecteur public prévu pour l'intégration (iframe). */
  readonly embedUrl: string;
  /** Page de la webcam : lien obligatoire selon les conditions du fournisseur. */
  readonly pageUrl: string;
  readonly credit: { readonly text: string; readonly url: string };
  /** Remarque honnête sur ce que l'image montre ou non. */
  readonly caveat: string;
  readonly verifiedAt: string;
}

// ── Libellés ──────────────────────────────────────────────────────────────────

export const SUNSET_LEVEL_LABELS: Readonly<Record<SunsetLevel, string>> = {
  dull: 'Terne',
  modest: 'Discret',
  good: 'Beau',
  great: 'Spectaculaire',
};

export const LAUNDRY_LEVEL_LABELS: Readonly<Record<LaundryLevel, string>> = {
  excellent: 'Excellent',
  good: 'Bon',
  fair: 'Moyen',
  slow: 'Lent',
  risky: 'Risqué',
  avoid: 'À éviter',
};

export const UMBRELLA_LEVEL_LABELS: Readonly<Record<UmbrellaLevel, string>> = {
  useless: 'Inutile',
  handy: 'Utile',
  needed: 'Indispensable',
};

export const JACKET_LEVEL_LABELS: Readonly<Record<JacketLevel, string>> = {
  none: 'T-shirt',
  light: 'Veste légère',
  jacket: 'Veste',
  coat: 'Manteau',
  warm_coat: 'Manteau chaud',
};

export const BIKE_LEVEL_LABELS: Readonly<Record<BikeLevel, string>> = {
  ideal: 'Idéal',
  good: 'Bon',
  fair: 'Moyen',
  avoid: 'À éviter',
};

/** Intitulé court d'un indice, pour les listes. */
export const INDEX_LABELS: Readonly<Record<DailyIndex['kind'], string>> = {
  laundry: 'Linge dehors',
  umbrella: 'Parapluie',
  jacket: 'Tenue',
  bike: 'Vélo',
};
